import { useMutation, useQueryClient } from '@tanstack/react-query'

import { BankService } from '@/api/services/BankService'
import { IBankInstitution } from '@/types'
import { INSTITUTIONS_QUERY_KEY } from './useInstitutions'

interface UpdateDisplayOrderVariables {
  institutionId: string
  displayOrder: number
}

export const useUpdateDisplayOrder = () => {
  const queryClient = useQueryClient()

  return useMutation<IBankInstitution | undefined, unknown, UpdateDisplayOrderVariables>({
    mutationFn: ({ institutionId, displayOrder }) =>
      BankService.updateInstitutionDisplayOrder(institutionId, displayOrder),
    // Written into the cached list straight away so the row settles without a flash of the old
    // value, then the list is read again in the background so the page shows what the service
    // actually stored (the order of other institutions included). The reload is not awaited: the
    // update has already succeeded, and a slow or stalled read should not hold the dialog open.
    // The requested order is set explicitly: proto JSON leaves a 0 out of the reply, which would
    // otherwise keep the old value.
    onSuccess: (updated, { institutionId, displayOrder }) => {
      queryClient.setQueryData<IBankInstitution[]>(INSTITUTIONS_QUERY_KEY, cached =>
        cached?.map(institution =>
          institution.id === institutionId
            ? { ...institution, ...updated, displayOrder }
            : institution
        )
      )
      void queryClient.invalidateQueries({ queryKey: INSTITUTIONS_QUERY_KEY })
    },
  })
}
